// ============================================================================
// OMS API — self-contained axios calls for the OMS dashboard.
// Uses the app's default axios instance (baseURL + JWT Authorization header are
// set globally in src/index.tsx), so relative paths inherit auth automatically.
// ============================================================================
import { message } from "antd";
import axios from "axios";
import { errorActions } from "../../helpers/auth_helper";
import {
  IncomingPurchaseOrderRow,
  ConfirmAvailabilityBatchBody,
  CrmIssueThread,
  ConfirmAvailabilityBody,
  ExpressUpgradeAllocationOutcome,
  OrderLogResponse,
  PaymentSyncResult,
  PendingAvailabilityGroupedResponse,
  PendingAvailabilityResponse,
  RedeliveryOptions,
  SalesOrderDetail,
  SalesOrderListResponse,
  SourcingOptions,
  SupplierSearchRow,
  SupplierSearchResponse,
} from "./types";

// The dashboard's cuts. Each is a SQL predicate on the server, so switching tabs paginates in
// the database instead of materialising every order to fold a derived status in python.
export type OmsTab =
  | "all"
  | "undelivered"
  | "delivered_open"
  | "delivered_not_invoiced"
  | "errors";

export interface OmsTabCounts {
  all: number;
  undelivered: number;
  delivered_open: number;
  delivered_not_invoiced: number;
  errors: number;
}

// The pipeline: every order sits in exactly ONE stage, so the stage totals sum to the order total.
export type OmsStage =
  | "to_source"
  | "procuring"
  | "ready_to_dispatch"
  | "in_transit"
  | "service_to_confirm"
  | "delivered_not_billed"
  | "payment_unsettled"
  | "delivered_open"
  | "completed"
  | "cancelled"
  | "anomaly";

// Action queues: independent cuts an order can sit on several of, combinable with a stage.
export type OmsQueue =
  | "blocking_errors"
  | "sync_failures"
  | "overdue"
  | "on_hold"
  | "returns_open";

export type OmsStageCell = { total: number } & Record<OmsQueue, number>;

export interface OmsPipelineCounts {
  all: number;
  stages: Record<OmsStage, OmsStageCell>;
  queues: Record<OmsQueue, number>;
}

export interface ListSalesOrdersParams {
  tab?: OmsTab;
  stage?: OmsStage;
  queue?: OmsQueue;
  sort?: "newest" | "promised";
  mode?: string;
  search?: string; // SO number / lead id / client phone — free-text lookup for the list screen
  // Narrow to orders carrying an error, by kind. "generic" is the critical view — a real failure
  // someone has to fix, with the payment/settlement waits (which clear themselves once the money
  // lands) filtered out. "invoice" is those waits; "any" is both.
  limit?: number;
  offset?: number;
  /** "allowed" | "blocked": the line's variant is / is not on the sellable (auto-fill) list. */
  auto_fill?: "allowed" | "blocked";
}

export async function listSalesOrders(
  params: ListSalesOrdersParams = {}
): Promise<SalesOrderListResponse | undefined> {
  try {
    return await axios
      .get("/oms/sales-orders/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading OMS sales orders");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export async function getSalesOrder(
  salesOrderId: number
): Promise<SalesOrderDetail | undefined> {
  try {
    return await axios
      .get(`/oms/sales-orders/${salesOrderId}/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading OMS sales order");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// The order's activity log — every recorded fact rendered into English by the server, plus the
// human actions (availability confirms, escalations raised, proofs uploaded) that were never
// events at all. Fetched when the drawer opens rather than with the order: most orders are never
// asked for their log, and a busy one carries a few hundred rows.
export async function getSalesOrderLog(
  salesOrderId: number
): Promise<OrderLogResponse | undefined> {
  try {
    return await axios
      .get(`/oms/sales-orders/${salesOrderId}/events/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading the order log");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Availability desk — lines placement couldn't source, and the picklist + write
// that resolve them. Until a line is confirmed here nothing moves on it: no
// allocation, no PO, and no error either — it just sits.
// ---------------------------------------------------------------------------

export interface ListPendingAvailabilityParams {
  estimate_id?: number;
  sales_order_id?: number;
  search?: string; // lead id / product / client phone
  limit?: number;
  offset?: number;
}

export async function listPendingAvailability(
  params: ListPendingAvailabilityParams = {}
): Promise<PendingAvailabilityResponse | undefined> {
  try {
    return await axios
      .get("/oms/availability/pending/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading pending availability");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// The same queue grouped by enquiry. This is what the desk renders: an operator works a whole
// lead in one form and submits one `confirmAvailabilityBatch`, because the planner can only merge
// demand into a shared allocation/PO within a single request. Paginated by ORDER, so a lead is
// never split across pages — which line-level paging would do, forcing exactly the piecemeal
// confirmation that fragments the POs.
export type AutoFillFilter = "all" | "allowed" | "blocked";

export async function listPendingAvailabilityGrouped(
  params: ListPendingAvailabilityParams = {}
): Promise<PendingAvailabilityGroupedResponse | undefined> {
  try {
    return await axios
      .get("/oms/availability/pending/grouped/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading pending availability");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// Where this line's goods could come from. Fetched when the operator opens a row rather than with
// the queue — most rows are never opened, and the options are a per-variant lookup.
// The open stock-PO lines for one pending line's variant: the optional "Incoming PO" picker on
// the confirm dialog. Same rows the procurement cart's incoming-PO panel shows.
export async function getIncomingPoOptions(
  lineId: number
): Promise<IncomingPurchaseOrderRow[]> {
  try {
    return await axios
      .get(`/oms/availability/lines/${lineId}/incoming-po-options/`)
      .then((res) => (Array.isArray(res?.data) ? res.data : []))
      .catch((error) => {
        errorActions(error, "Error loading incoming PO options");
        return [];
      });
  } catch (e) {
    return [];
  }
}

export async function getSourcingOptions(
  lineId: number
): Promise<SourcingOptions | undefined> {
  try {
    return await axios
      .get(`/oms/availability/lines/${lineId}/sourcing-options/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading sourcing options");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// Every active supplier with its active warehouses — the "pick another vendor" list on the
// availability desk. `getSourcingOptions` returns only the variant's preferred panel, which is the
// right default but was also the only reachable choice; the server's confirm gate has never
// required panel membership.
//
// Fetched WHOLE and filtered in the browser. There are a few hundred active suppliers, so a
// server-side search would spend a round trip per keystroke to narrow a list small enough to hold;
// one fetch makes typing instant. Cached briefly because every line of an order needs the same
// list and the supplier base barely moves within a session.
//
// `variantId` flags panel suppliers `is_preferred` — so the cache is keyed by it.
const SUPPLIERS_TTL_MS = 5 * 60 * 1000;
const supplierCache = new Map<string, { at: number; rows: Promise<SupplierSearchRow[]> }>();

export function listActiveSuppliers(variantId?: number): Promise<SupplierSearchRow[]> {
  const key = String(variantId ?? "");
  const hit = supplierCache.get(key);
  if (hit && Date.now() - hit.at < SUPPLIERS_TTL_MS) return hit.rows;

  const rows = axios
    .get("/oms/availability/suppliers/", { params: { variant_id: variantId } })
    .then((res) => (res?.data as SupplierSearchResponse)?.results ?? [])
    .catch((error: any) => {
      supplierCache.delete(key); // never cache a failure — the next keystroke should retry
      errorActions(error, "Error loading suppliers");
      return [] as SupplierSearchRow[];
    });
  supplierCache.set(key, { at: Date.now(), rows });
  return rows;
}

// Confirm one line: writes the sourcing onto it, then plans + allocates. Resolves to the refreshed
// SalesOrderDetail, or undefined when the server refuses (its {detail} is surfaced via antd) —
// e.g. a negative lead time, or a supplier warehouse with no supplier to procure from.
export async function confirmAvailability(
  body: ConfirmAvailabilityBody
): Promise<SalesOrderDetail | undefined> {
  try {
    const res = await axios.post("/oms/availability/confirm/", body);
    message.success("Availability confirmed");
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not confirm availability");
    return undefined;
  }
}

// Confirm several lines of ONE order at once. Prefer this over looping `confirmAvailability`:
// the server plans the whole batch together, so lines sharing a supplier collapse into a single
// allocation and a single PO. All-or-nothing — a rejected batch changes nothing, and its {detail}
// lists every problem found.
export async function confirmAvailabilityBatch(
  body: ConfirmAvailabilityBatchBody
): Promise<SalesOrderDetail | undefined> {
  try {
    const res = await axios.post("/oms/availability/confirm-batch/", body);
    const n = body.items.length;
    message.success(`Availability confirmed for ${n} line${n === 1 ? "" : "s"}`);
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not confirm availability");
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Escalations — post-placement mutations. Each POSTs to its action endpoint and
// resolves to the refreshed SalesOrderDetail on success, or undefined on failure
// (the endpoint's {detail} message is surfaced via antd). Thin: the service owns
// all the rules; the UI just collects fields and shows the result.
// ---------------------------------------------------------------------------
async function postEscalation(
  url: string,
  body: Record<string, unknown>,
  successMsg: string
): Promise<SalesOrderDetail | undefined> {
  try {
    const res = await axios.post(url, body);
    message.success(successMsg);
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Action failed");
    return undefined;
  }
}

export const cancelOrder = (soId: number, reason: string) =>
  postEscalation(`/oms/sales-orders/${soId}/cancel/`, { reason }, "Order cancelled");

export const changeAddress = (soId: number, reason: string) =>
  postEscalation(`/oms/sales-orders/${soId}/change-address/`, { reason }, "Shipping address updated");

// Cancel a qty OFF the customer's order. Reaches only quantity NO allotment holds — deallocate it
// first. Everything the old cancel had to know (amend the PO, open a packed carton, revise a truck)
// belongs to the deallocation that runs before it, which is why this takes nothing but a quantity.
// The backend refuses with a readable 400 otherwise, and refuses again if this would empty the whole
// order — that is `cancelOrder`, which voids the sales order and unwinds its tail.
export const cancelItem = (
  lineId: number,
  body: { qty: number; reason?: string }
) => postEscalation(`/oms/sales-order-lines/${lineId}/cancel/`, body, "Item cancelled");

export const returnItems = (
  lineId: number,
  body: {
    qty: number;
    replace?: boolean;
    restock?: boolean;
    return_wh_id?: number;
    reason?: string;
    // Operator-chosen pickup date (YYYY-MM-DD). Required for returns — stamped onto the pickup
    // stage's due_at server-side so Loginext auto-booking honors it as pickupStartTimeWindow.
    pickup_date: string;
    // Pre-authorize refund at raise (gated by refund.before_return on the backend). When supplied,
    // the server spawns a REFUND_FLOW immediately with a damage-shape money-only CN; the CN is
    // amended with the real items + stock when QC completes.
    loading?:
      | { required: false }
      | { required: true; lift_available: boolean; floor_no: number };
    authorize_now?: {
      items: { allocation_item_id: number; qty: number; rate_incl_tax: number; product_name?: string }[];
      destination: "wallet" | "customer";
      bank_details_id?: number | null;
      note?: string;
    };
  }
) =>
  postEscalation(
    `/oms/sales-order-lines/${lineId}/return/`,
    body,
    body.replace ? "Replacement filed" : "Return filed"
  );

// --- refunds ---------------------------------------------------------------------------------
// Both money-back exits for DEALLOCATED quantity. They reach only quantity a deallocation handed
// back, and both drop it off demand — what differs is where the money goes:
//   wallet   → a linked faux invoice + credit note in Zoho, then a wallet credit that becomes
//              spendable once the credit note lands (about a minute later, via the pipeline).
//   customer → SO_ITEM_CANCEL in Zoho, and the money drained back across the order's payments in a
//              fixed order (credit first, then Razorpay, then wallet/CN, then anything manual).
// A 400 here is an operator-facing sentence, not a code — surface it verbatim.
export type RefundDestination = "wallet" | "customer";

// Not `postEscalation`: this endpoint answers with what it ACTUALLY did, which is not always what
// was asked. A wallet refund on a customer who still owes credit is converted into a credit
// reversal, and the operator has to be told — so the server's own sentence is the toast, and a
// conversion is a warning rather than a success.
export const refundLine = async (
  lineId: number,
  body: {
    qty: number; destination: RefundDestination; reason_code?: string; notes?: string;
    // Required when destination=customer — the UserBankDetails row id for the payout. Null for
    // wallet-destination refunds. Backend refuses customer-destination submits without it.
    bank_details_id?: number | null;
  }
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-order-lines/${lineId}/refund/`, body);
    const data = res?.data ?? {};
    if (data.converted) {
      message.warning(data.message, 8);
    } else {
      message.success(data.message ?? "Refund raised");
    }
    return data.sales_order as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Action failed");
    return undefined;
  }
};

// Batch counterpart, scoped to ONE sales order — cuts one Zoho invoice + one credit note covering
// every refunded line on the wallet path. Same response shape as `refundLine` (the "converted"
// flag flips a success toast to a warning). Fails as ONE — a bad line refuses the whole batch on
// the server, so the operator retries once instead of untangling a half-refund.
export const refundBatch = async (
  salesOrderId: number,
  body: {
    // `unit_price_rupees` is the operator-editable per-unit rate (tax-inclusive, ₹) from the modal.
    // Omitted → backend prices the line off the live Zoho SO as before; present → the faux invoice
    // + CN are raised at that rate. The `_payment_covers` gate is unchanged: it just sees the new
    // total and refuses anything above paid+tolerance.
    items: { line_id: number; qty: number; unit_price_rupees?: string }[];
    additional_charges?: {
      salesorder_item_id: string;
      // Operator-editable GST-inclusive rupee rate per unit. Omitted = Zoho's own `rate`.
      unit_price_incl_tax?: string;
    }[];
    destination: RefundDestination;
    reason_code?: string;
    notes?: string;
    // Required when destination=customer — the UserBankDetails row id for the payout.
    // Backend refuses customer-destination submits without it.
    bank_details_id?: number | null;
    // Inline-added account from the picker; overrides `destination` + `bank_details_id` when set.
    destination_block?: {
      destination: "wallet" | "customer";
      new_bank_details: { account_holder_name: string; account_number: string; ifsc: string; bank_name: string };
    };
  }
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/${salesOrderId}/refund-batch/`, body);
    const data = res?.data ?? {};
    if (data.converted) {
      message.warning(data.message, 8);
    } else {
      message.success(data.message ?? "Refund raised");
    }
    return data.sales_order as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Action failed");
    return undefined;
  }
};

// Live from Zoho: the SO's service-charge lines that still have refundable qty. Called by the
// refund modal on open — Zoho itself decides eligibility (`quantity_cancelled < quantity`), so a
// charge already refunded on a prior pass is simply not returned.
export interface RefundableChargesResult {
  rows: import("./types").RefundableCharge[];
  // Set when the backend call failed (Zoho outage / throttle / 5xx) so the modal can distinguish
  // "no charges on this SO" from "we couldn't ask Zoho right now". Operator-facing short message.
  fetchError?: string;
}

export const getRefundableCharges = async (
  salesOrderId: number
): Promise<RefundableChargesResult> => {
  try {
    const res = await axios.get(`/oms/sales-orders/${salesOrderId}/refundable-charges/`);
    return { rows: (res?.data?.results ?? []) as import("./types").RefundableCharge[] };
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    const msg = typeof detail === "string" && detail
      ? detail
      : "Could not fetch refundable charges — Zoho may be unreachable or rate-limiting right now. Retry in a moment.";
    return { rows: [], fetchError: msg };
  }
};

// Per-unit refund price for each still-refundable goods line — priced live off the Zoho SO so
// the modal total matches what the backend will actually credit (item- and order-level discounts
// baked in, tax added back). Silent on failure: the modal falls back to `unit_selling_price`,
// which is only wrong on orders that carried an order-level discount at placement.
//
// `credit_outstanding_paise` is piggybacked onto the same call: how much the customer still owes
// on credit on this estimate. The modal uses it to compute the ADR 2026-10-13 shrink/excess split
// — hiding the destination picker when the refund will fully shrink credit, and labelling the
// picker to say what the excess (not the full refund) is being routed to.
export interface RefundableLinesResponse {
  rows: import("./types").RefundableLinePrice[];
  credit_outstanding_paise: string;
}

export const getRefundableLines = async (
  salesOrderId: number,
  allLines = false
): Promise<RefundableLinesResponse> => {
  try {
    const res = await axios.get(`/oms/sales-orders/${salesOrderId}/refundable-lines/`,
      allLines ? { params: { all: 1 } } : undefined);
    return {
      rows: (res?.data?.results ?? []) as import("./types").RefundableLinePrice[],
      credit_outstanding_paise: String(res?.data?.credit_outstanding_paise ?? "0"),
    };
  } catch (error: any) {
    return { rows: [], credit_outstanding_paise: "0" };
  }
};

// P2P, not express: re-routes this allotment to dropship and leaves the order's mode alone. Only
// "Express this order" marks an order express.
export const upgradeP2P = (allocationId: number, reason: string) =>
  postEscalation(`/oms/allocations/${allocationId}/upgrade-p2p/`, { reason }, "Upgraded to P2P");

// The two date moves. Reschedule delivery moves the customer's committed date and only the delivery
// leg; reschedule pickup moves when the goods become available and re-plans the open legs from it.
// Both go either way; feasibility is server-side (400 with an operator-readable detail).
export const rescheduleDelivery = (
  allocationId: number,
  body: { target_date: string; reason?: string }
) =>
  postEscalation(
    `/oms/allocations/${allocationId}/reschedule-delivery/`,
    body,
    "Delivery rescheduled"
  );

export const reschedulePickup = (
  allocationId: number,
  body: { new_date: string; reason?: string }
) =>
  postEscalation(
    `/oms/allocations/${allocationId}/reschedule-pickup/`,
    body,
    "Pickup rescheduled"
  );

// What a reattempt would do for this allotment — which mode the backend will take, and (in
// redelivery mode) what is still available to send out again. Fetched when the operator opens the
// action, not with the order: most allotments are never reattempted.
export async function getRedeliveryOptions(
  allocationId: number
): Promise<RedeliveryOptions | undefined> {
  try {
    return await axios
      .get(`/oms/allocations/${allocationId}/reattempt-delivery/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading redelivery options");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// A reattempt on a still-in-transit order is guarded: the backend answers 409
// { requires_confirmation: true, delivery_status: "in_transit", detail } instead of acting, and the
// operator must confirm (with a mandatory reason) and re-POST with confirm_in_transit: true. So this
// can't use postEscalation (which folds every non-2xx into a generic failure) — it returns a small
// discriminated result the caller switches on. A plain 400 is still a real validation error.
export type ReattemptResult =
  | { status: "ok"; so: SalesOrderDetail }
  | { status: "needs_confirmation"; detail: string }
  | { status: "error" };

export const reattemptDelivery = async (
  allocationId: number,
  // `target_delivery_date` (optional, ISO YYYY-MM-DD) schedules the reattempt for that day; omit it to
  // re-book for the earliest slot (today).
  //
  // `items` applies to the REDELIVERY mode only (the leg already delivered): the goods left behind,
  // capped server-side at what is still redeliverable. Omit it to send the whole remainder; the
  // backend ignores it when it re-points an attempt that never arrived.
  //
  // `confirm_in_transit` is set only on the second POST, after the operator confirms pulling an
  // in-transit order off its live run; `reason` is then mandatory (it drives the delivery-team
  // escalation the backend sends).
  body: {
    current_wh_id: number;
    target_delivery_date?: string;
    items?: { allocation_item_id: number; qty: number }[];
    reason?: string;
    confirm_in_transit?: boolean;
  }
): Promise<ReattemptResult> => {
  try {
    const res = await axios.post(
      `/oms/allocations/${allocationId}/reattempt-delivery/`,
      body
    );
    message.success("Delivery reattempt booked");
    return { status: "ok", so: res?.data as SalesOrderDetail };
  } catch (error: any) {
    const data = error?.response?.data;
    // Still on a live truck → the backend wants an explicit confirmation + reason, not a failure.
    if (
      error?.response?.status === 409 &&
      data?.requires_confirmation &&
      data?.delivery_status === "in_transit"
    ) {
      return {
        status: "needs_confirmation",
        detail:
          data.detail ||
          "This order is still in transit. Confirm to pull it off its current run.",
      };
    }
    message.error(data?.detail || "Action failed");
    return { status: "error" };
  }
};

// SET (not add) the order's unloading charge to `amount` — gross rupees incl. 18% GST. A resubmit
// REPLACES the existing charge and moves the payable by the difference, so raising it holds an
// unpaid order at dispatch until the difference is collected. 400 on a zero/negative/malformed
// amount. On an already-invoiced order the SO-sync step of the spawned escalation shows skipped —
// expected (the next invoice bills the charge standalone), not an error.
//
// `payment` (optional) records the money collected for the charge IN THE SAME REQUEST — the same
// payment_method / payment_mode vocabulary the estimate-payment-information API takes, nested
// verbatim (payment_amount defaults to the charge; collector / reference / PAN / waive-off rules
// are enforced server-side). The response then carries `recorded_payment`.
export const addUnloadingCharge = (
  salesOrderId: number,
  body: { amount: number; reason?: string; payment?: Record<string, unknown> }
) =>
  postEscalation(
    `/oms/sales-orders/${salesOrderId}/add-unloading-charge/`,
    body,
    "Unloading charge set"
  );

// Express the WHOLE order: the mode flips to express and every eligible backorder allotment is
// re-routed to dropship — ineligible ones are SKIPPED, not failed, with per-allotment verdicts on
// the spawned `express_upgrade_order` escalation's params.allocations (plus one `express_upgrade`
// escalation per re-routed allotment — expected, not duplicates). `charge_amount` (gross rupees
// incl. GST) optionally bills an express charge; omit for a free upgrade. 400 only on a
// malformed/negative charge. Not a plain postEscalation because the toast should headline the
// per-allotment outcome — "expressed" alone would hide a skip the operator ought to notice.
export const upgradeExpress = async (
  salesOrderId: number,
  // `payment` as on addUnloadingCharge: the optional inline payment for the express charge,
  // recorded in the same request and echoed back as `recorded_payment`.
  body: { charge_amount?: number; reason?: string; payment?: Record<string, unknown> }
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/${salesOrderId}/upgrade-express/`, body);
    const so = res?.data as SalesOrderDetail;
    const outcomes = ((so?.escalations ?? [])
      .filter((e) => e.type === "express_upgrade_order")
      .sort((a, b) => b.id - a.id)[0]?.params?.allocations ??
      []) as ExpressUpgradeAllocationOutcome[];
    const upgraded = outcomes.filter((o) => o.outcome === "upgraded").length;
    const skipped = outcomes.length - upgraded;
    message.success(
      outcomes.length
        ? `Order expressed — ${upgraded} allotment${upgraded === 1 ? "" : "s"} upgraded` +
            (skipped ? `, ${skipped} skipped (see Escalations for why)` : "")
        : "Order expressed"
    );
    return so;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not express the order");
    return undefined;
  }
};

// The reasons a quantity comes off an allotment. Recorded on the escalation, and the thing that
// makes "how much did we buy but not sell, and why" answerable — the damage codes in particular,
// since damage is no longer an action of its own but a deallocation that leaves the PO standing.
// Why money is going back to the customer. Mirrors `oms.refund.models.RefundReason` — the
// queryable half of the record, beside the operator's free-text note.
export const REFUND_REASONS: { value: string; label: string }[] = [
  { value: "transit_damage", label: "Transit damage" },
  { value: "wrong_material", label: "Wrong material" },
  { value: "missing_material", label: "Missing material" },
  { value: "product_quality_issue", label: "Product Quality Issue" },
  { value: "client_preference", label: "Client preference" },
  { value: "full_or_partial_cancellation", label: "Full/Partial cancellation" },
  { value: "material_delay", label: "Material delay" },
  { value: "delivery_delay", label: "Delivery delay" },
  { value: "installation_delay", label: "Installation delay" },
];

export const DEALLOCATION_REASONS: { value: string; label: string }[] = [
  { value: "supplier_short", label: "Supplier delivered short" },
  { value: "supplier_cannot_supply", label: "Supplier cannot supply" },
  { value: "partial_availability", label: "Only part available now" },
  { value: "stock_unavailable", label: "Reserved stock not actually free" },
  { value: "damage_in_transit", label: "Damaged in transit to us" },
  { value: "damage_in_warehouse", label: "Damaged in our warehouse" },
  { value: "wrong_sourcing", label: "Wrong supplier, warehouse or split" },
  { value: "false_receipt", label: "Marked received but never arrived" },
  { value: "false_dispatch", label: "Marked dispatched but never left" },
  { value: "false_delivery", label: "Marked delivered but never delivered" },
  { value: "customer_cancelled", label: "Customer cancelled" },
  { value: "other", label: "Other" },
];

// Deallocate — the ONE action that changes quantity. Pull a quantity back OFF this allotment, item by
// item. The allotment shrinks (its legs shrink with it, and are cancelled outright once it holds
// nothing) and the line goes back on the AVAILABILITY DESK to be sourced again — nobody names a
// replacement supplier here, which is the whole point.
//
// It never touches the customer's order: no cancelled quantity, no sales-order edit. Taking the qty
// OFF the order is `cancelItem`, a separate escalation that this one makes possible.
//
// Per item, `cancel_po` (default true) settles the purchase order — amended down pre-receipt,
// short-closed at a GRN, deleted on a dropship — while false leaves it standing, so the goods still
// arrive and become MD stock. That is the damage case.
// Where a quantity may be unwound TO, per allotment item. A deallocation rolls back to a PLACE and
// the operator names it — the choice is limited to the places these goods have actually been, and it
// decides how far back the unwind runs. An item with EMPTY options cannot be deallocated at all.
export interface DeallocationDestination {
  value: string; // "supplier" | "wh:<id>"
  label: string;
  kind: "supplier" | "warehouse";
}

export interface DeallocationItemOptions {
  allocation_item_id: number;
  sales_order_line_id: number;
  qty: string;
  options: DeallocationDestination[];
  default: string | null;
  /** Why this item has nowhere to go — set exactly when `options` is empty. The deallocation
   *  refuses on this ground, so show the reason rather than an empty picker. */
  blocked: string | null;
}

export async function getDeallocationOptions(
  allocationId: number
): Promise<DeallocationItemOptions[] | undefined> {
  try {
    return await axios
      .get(`/oms/allocations/${allocationId}/deallocation-options/`)
      .then((res) => res?.data?.items ?? [])
      .catch((error) => {
        errorActions(error, "Could not load where these goods can go");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export const deallocateAllocation = (
  allocationId: number,
  body: {
    items: {
      allocation_item_id?: number;
      sales_order_line_id?: number;
      qty: number;
      // WHERE the goods come to rest. Back to the supplier rolls the purchase order back with them;
      // one of our warehouses leaves it standing, which is what damaged units want.
      destination: string;
    }[];
    reason_code?: string;
    reason?: string;
  }
) =>
  postEscalation(
    `/oms/allocations/${allocationId}/deallocate/`,
    body,
    "Quantity returned to the availability desk"
  );

// Re-state what an allotment is BUYING after the supplier re-measures their own delivery. `qty` is
// the MEASURED figure, absolute (not a change), and may be higher or lower than what was ordered.
// The purchase order and the goods-inwards legs move to it; the customer's quantity, package and
// invoice do not — the difference is a deliberate inventory variance. Backorders only.
export const modifyPo = (
  allocationId: number,
  body: {
    items: {
      allocation_item_id?: number;
      sales_order_line_id?: number;
      qty: number;
    }[];
    reason?: string;
  }
) =>
  postEscalation(
    `/oms/allocations/${allocationId}/modify-po/`,
    body,
    "Purchase order re-stated at the measured quantity"
  );

// Re-send the supplier PO notification (email + WhatsApp) for ONE allocation's PO. A manual retry of
// a benign, repeatable action — the PO already exists in Zoho, we just re-fire the vendor dispatch.
// The result is NOT an SO refresh, so this resolves to `true` (success, nothing to refresh) to close
// the action modal, or `undefined` on failure. Crucially, a `deferred` result means the backend
// notify switch is still off and NOTHING actually went out — surfaced as a warning, never a success,
// so the desk isn't misled. Same for a PO not yet created in Zoho (`skipped`).
// Reconcile a Zoho invoice with what we actually charged. Legacy floors the payable to a whole
// rupee and Zoho does not, so the invoice can sit a few paise short of settled and hold the shipment
// even though the customer has paid in full. Not an escalation — nothing about the order changes;
// it stamps a rounding adjustment on the invoice and releases the stranded payment document.
//
// The backend refuses (409) whenever the gap is NOT a rounding artefact — a real shortfall, or an
// order that is only part-invoiced — so this can never write off money that is genuinely owed. That
// refusal is surfaced verbatim, since it means someone needs to look at the order rather than retry.
export const settleInvoiceRounding = async (
  allocationId: number
): Promise<true | undefined> => {
  try {
    const res = await axios.post(`/oms/allocations/${allocationId}/settle-rounding/`);
    const r = res?.data ?? {};
    if (!r.adjusted) {
      message.info("Invoice already matches the amount collected — nothing to adjust.");
    } else {
      message.success(`Invoice adjusted by ₹${Math.abs(r.adjustment ?? 0).toFixed(2)} — shipment released`);
    }
    return true;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not settle the invoice rounding");
    return undefined;
  }
};

export const resendSupplierNotification = async (
  allocationId: number
): Promise<true | undefined> => {
  try {
    const res = await axios.post(
      `/oms/overrides/allocations/${allocationId}/resend-supplier-notification/`
    );
    const r = res?.data ?? {};
    if (r.skipped === "po_not_in_zoho") {
      message.warning("PO isn't in Zoho yet — nothing to send. Retry once the PO has been created.");
    } else if (r.deferred) {
      message.warning(
        "Logged only — nothing sent. Supplier notifications are switched off for this environment " +
          "(OMS_SUPPLIER_NOTIFY_BACKEND is not 'periskope')."
      );
    } else if (r.sent) {
      const via = [r.email && "email", r.whatsapp && "WhatsApp"].filter(Boolean).join(" + ");
      message.success(`Supplier notified${via ? ` via ${via}` : ""}${r.po_number ? ` (PO# ${r.po_number})` : ""}`);
    } else {
      message.error(r.error ? `Could not notify supplier: ${r.error}` : "Supplier notification failed");
    }
    return true;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not resend supplier notification");
    return undefined;
  }
};

export const pickComplete = (stageId: number) =>
  postEscalation(`/oms/stages/${stageId}/pick-complete/`, {}, "Pick confirmed");

// A SERVICE leg has no dispatch, so this confirmation is what BILLS it: completing the leg raises
// the invoice for the work. `notes` is optional free text kept on the stage's event log.
export const serviceComplete = (stageId: number, notes?: string) =>
  postEscalation(
    `/oms/stages/${stageId}/service-performed/`,
    { notes: notes ?? "" },
    "Service confirmed — invoice raised"
  );

// Release a HELD customizable allotment (wallpaper, wooden flooring, …) into production. Posted to
// the allotment's `go_ahead_stage_id`, NOT per line — one call releases every awaiting line on the
// allotment together. The backend starts the ~4 working-day production lead time and moves the
// order's delivery dates forward; the refreshed SalesOrderDetail it returns already carries the new
// dates and the cleared badges, so the caller re-renders from it (no refetch, no guessing dates).
// Idempotent server-side — a double click is safe. `notes` is optional, kept on the event + timeline.
//
// `lineQuantities` is the FINAL measured quantity (e.g. sq ft) per sales_order_line_id, collected on
// approval. It revises the VENDOR PO only — it never changes what the customer is billed, so don't
// expect the customer's price/qty to move in the response. Omit it / pass {} to approve the render
// AS ORDERED with no PO change. A bad quantity (non-positive, or a line that can't be re-measured),
// like a non-customization allotment, comes back as a 400 whose {detail} postEscalation surfaces.
export const giveGoAhead = (
  stageId: number,
  lineQuantities?: Record<number, number>,
  notes?: string
) =>
  postEscalation(
    `/oms/stages/${stageId}/customization-approved/`,
    { line_quantities: lineQuantities ?? {}, notes: notes ?? "" },
    "Go-ahead given — production started"
  );

export interface ReconcilePending {
  picklists: number;
  receives: number;
  total: number;
}

export interface ReconcileResult {
  replayed: { picklists: number; receives: number };
  pending: ReconcilePending;
  detail?: string;
}

// How many open legs a reconcile would poll. Pure DB read server-side — costs no Zoho calls,
// so it is safe to fetch on render.
export const fetchReconcilePending = async (): Promise<
  ReconcilePending | undefined
> => {
  try {
    const res = await axios.get("/oms/reconcile-inbound/");
    return res?.data as ReconcilePending;
  } catch {
    return undefined;
  }
};

export const reconcileInbound = async (): Promise<
  ReconcileResult | undefined
> => {
  try {
    const res = await axios.post("/oms/reconcile-inbound/");
    const data = res?.data as ReconcileResult;
    const { picklists = 0, receives = 0 } = data?.replayed ?? {};
    if (!picklists && !receives) {
      message.info(data?.detail || "Nothing to reconcile.");
    } else {
      message.success(
        `Reconciled ${picklists} picklist(s) and ${receives} receive(s) from Zoho`
      );
    }
    return data;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Reconcile failed");
    return undefined;
  }
};

// ── Zoho billing identity (B2B vs B2C) ────────────────────────────────────────
// The billing-side twin of the inbound reconcile. A conversion that half-landed — the sales order
// re-pointed, an invoice's PUT lost to a 502 — leaves the order's Zoho documents disagreeing about
// who is being billed. `wrong_fields` is per document, because WHICH document is wrong is the whole
// question: the SO can be correct while the invoice the customer holds is not.
export interface BillingDriftField {
  field: "gst_no" | "gst_treatment" | "customer_id";
  expected: string;
  actual: string;
}

export interface BillingDriftDoc {
  kind: "sales_order" | "invoice" | "delivery_challan";
  zoho_doc_id: string;
  document_id: number | null;
  number: string;
  customer_id: string;
  customer_name: string;
  gst_no: string;
  gst_treatment: string;
  in_sync: boolean;
  wrong_fields: BillingDriftField[];
}

// Where this order's money sits, when an invoice is still unsettled. The pre-ship gate looks up
// customer payments scoped to the contact the estimate resolves to now, so a payment recorded
// against a DIFFERENT contact is invisible to it — the invoice never settles and the order wears the
// amber "Invoice Error" while every document bills the right identity. The re-sync does not move
// money; `movePaymentContact` is what repairs this.
export interface BillingDriftPayment {
  reference: string;
  payment_id: string | null;
  customer_id: string;
  customer_name: string;
  amount: number;
  unused_amount: number;
  in_zoho: boolean;
  // null when no Zoho contact is cached for the customer — unknown, never "wrong".
  on_expected_contact: boolean | null;
}

export interface BillingDriftSettlement {
  unpaid_invoices: { zoho_doc_id: string; number: string; balance: number }[];
  payments: BillingDriftPayment[];
  misplaced_count: number; // in Zoho, but under another contact
  missing_count: number; // not in Zoho at all — the capture-time push never landed
  unreadable: string[];
  truncated: number;
  expected_contact_known: boolean;
}

export interface BillingDrift {
  sales_order_id: number;
  expected: {
    billing: "b2b" | "b2c";
    gstin: string;
    gst_treatment: string;
    contact_id: string | null;
    bill_address_id: number | null;
  };
  documents: BillingDriftDoc[];
  unreadable: { kind: string; zoho_doc_id: string; reason: string }[];
  // null when every invoice is settled (or there is none). Deliberately NOT a term in `in_sync` —
  // it describes money the re-sync does not touch.
  settlement: BillingDriftSettlement | null;
  // False when anything drifted OR any document could not be read — an unreadable document is not
  // evidence of agreement.
  in_sync: boolean | null;
  out_of_sync_count: number;
  contact_checked: boolean;
  blocking_reasons?: string[];
  escalation_id?: number;
  document_states?: Record<string, string>;
  detail?: string;
}

// Read-only: makes no Zoho writes and will not create a contact, so it is safe to call on open.
export const fetchBillingDrift = async (
  salesOrderId: number
): Promise<BillingDrift | undefined> => {
  try {
    const res = await axios.get(
      `/oms/sales-orders/${salesOrderId}/reconcile-billing/`
    );
    return res?.data as BillingDrift;
  } catch (error: any) {
    message.error(
      error?.response?.data?.detail || "Could not read the order's Zoho billing state"
    );
    return undefined;
  }
};

// Forces the order's Zoho documents onto the billing identity its estimate already names. Takes no
// target and is idempotent, so it repairs only what drifted and is safe to press again.
export const reconcileBilling = async (
  salesOrderId: number,
  reason?: string
): Promise<BillingDrift | undefined> => {
  try {
    const res = await axios.post(
      `/oms/sales-orders/${salesOrderId}/reconcile-billing/`,
      reason ? { reason } : {}
    );
    const data = res?.data as BillingDrift;
    if (data?.in_sync) {
      message.success("Zoho billing re-synced — every document now agrees");
    } else if (data?.in_sync === null) {
      message.warning(data?.detail || "Re-sync ran; verification could not be read", 8);
    } else {
      message.warning(
        `Re-sync ran but ${data?.out_of_sync_count ?? 0} document(s) still disagree — check the list`,
        8
      );
    }
    return data;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Billing re-sync failed", 8);
    return undefined;
  }
};

// ── Moving money onto the right Zoho contact ──────────────────────────────────
// Zoho cannot re-point a customer payment — the customer is fixed at creation — so this deletes it
// and re-records it from the payment transaction, which carries the original payment date and the
// same reference number. Offered in the billing modal but NOT behind that action's blocking reasons:
// those exist because re-billing a document changes what a customer owes under a GSTIN, none of
// which is true here, and sharing them would put this out of reach on an invoiced order from a
// previous month — the common shape of this fault.
export interface PaymentMoveRow {
  reference: string;
  payment_id: string | null;
  customer_id: string;
  customer_name: string;
  amount: number;
  transaction_id?: number;
  reason?: string; // present on `blocked` rows: why this payment is being left alone
}

export interface PaymentMovePlan {
  sales_order_id: number;
  target_contact: string | null;
  movable: PaymentMoveRow[];
  // Refused, each with a reason — shown rather than hidden, so "nothing moved" is never silent.
  blocked: PaymentMoveRow[];
  unpaid_invoices?: { zoho_doc_id: string; number: string; balance: number }[];
  unreadable?: string[];
  detail?: string;
  moved?: {
    reference: string;
    transaction_id: number;
    old_payment_id: string;
    new_payment_id: string;
    from_contact: string;
    to_contact: string;
    amount: number;
  }[];
  requeued_documents?: number[];
  money_missing_in_zoho?: boolean;
}

// Read-only plan. Safe on open — no Zoho writes, no DB writes.
export const fetchPaymentMovePlan = async (
  salesOrderId: number
): Promise<PaymentMovePlan | undefined> => {
  try {
    const res = await axios.get(
      `/oms/sales-orders/${salesOrderId}/move-payment-contact/`
    );
    return res?.data as PaymentMovePlan;
  } catch (error: any) {
    message.error(
      error?.response?.data?.detail || "Could not read this order's payments from Zoho"
    );
    return undefined;
  }
};

export const movePaymentContact = async (
  salesOrderId: number,
  reason?: string
): Promise<PaymentMovePlan | undefined> => {
  try {
    const res = await axios.post(
      `/oms/sales-orders/${salesOrderId}/move-payment-contact/`,
      reason ? { reason } : {}
    );
    const data = res?.data as PaymentMovePlan;
    const n = data?.moved?.length ?? 0;
    if (n > 0) {
      message.success(
        `${n} payment(s) moved to the right contact${
          data?.requeued_documents?.length ? " — the invoice will settle on the next pass" : ""
        }`,
        8
      );
    } else {
      message.info(data?.detail || "No payment on this order is on the wrong contact", 6);
    }
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    // The one state that must not read as a normal failure: a payment was deleted and its
    // replacement did not land, so the money is not in Zoho at all. Held on screen until dismissed.
    if (body?.money_missing_in_zoho) {
      message.error(body.detail, 0);
    } else {
      message.error(body?.detail || "Could not move the payment", 8);
    }
    return undefined;
  }
};

// Retry differs from the other stage actions in what it reports: the useful information is
// WHICH documents/bookings were requeued — and, often, that there was nothing to retry at all.
export const retryStage = async (
  stageId: number
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/stages/${stageId}/retry/`);
    reportRetry(res?.data?.retry, "this stage");
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Retry failed");
    return undefined;
  }
};

// Order-level retry: requeues EVERY failed artifact on the order — the order-level SALES_ORDER doc
// (which no single stage owns, so a stage retry can't reach it) AND every leg's failed docs/bookings,
// in one action. The central "Retry" for a failed order. Same response shape as the stage retry.
export const retrySalesOrder = async (
  salesOrderId: number
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/${salesOrderId}/retry/`);
    reportRetry(res?.data?.retry, "this order");
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Retry failed");
    return undefined;
  }
};

// Bulk order-level retry for the rows on one dashboard page. One request, not one per row: the
// backend runs the same order retry over each id and skips orders that are already completed or
// cancelled (nothing on those can be requeued). Returns the summary so the caller can report it.
export interface BulkRetryResult {
  retried: number[];
  skipped: { sales_order_id: number; reason: string }[];
  documents_requeued: number;
  bookings_requeued: number;
}

export const retrySalesOrders = async (
  salesOrderIds: number[]
): Promise<BulkRetryResult | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/retry/`, {
      sales_order_ids: salesOrderIds,
    });
    const data = res?.data as BulkRetryResult;
    const docs = data?.documents_requeued ?? 0;
    const bookings = data?.bookings_requeued ?? 0;
    const orders = data?.retried?.length ?? 0;
    const skipped = data?.skipped?.length ?? 0;
    const tail = skipped ? ` (${skipped} already completed/cancelled — skipped)` : "";
    if (!docs && !bookings) {
      message.info(`Nothing to retry across ${orders} order(s)${tail}.`);
    } else {
      const parts = [];
      if (docs) parts.push(`${docs} document${docs > 1 ? "s" : ""}`);
      if (bookings) parts.push(`${bookings} booking${bookings > 1 ? "s" : ""}`);
      // Same caveat as the single retry: requeued now, re-sent by the background pass.
      message.success(
        `Requeued ${parts.join(" and ")} across ${orders} order(s)${tail} — retrying in the background, refresh shortly`
      );
    }
    return data;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Retry failed");
    return undefined;
  }
};

// Force this order's Zoho item masters back in step with the Variant table. The backend aligns them
// automatically ONCE, when the Sales Order is created — this is the manual re-push for a product that
// was renamed / re-priced / had its HSN fixed after the order was placed, or an item edited directly
// in Zoho. Reports per-item outcomes because a partial failure is the interesting case: the operator
// needs to know WHICH product did not sync.
// Returns true only when EVERY item synced — a partial or total failure returns undefined, which
// leaves the action's modal open so the operator can fix the named product and try again.
export const syncOrderItems = async (
  salesOrderId: number
): Promise<true | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/${salesOrderId}/sync-items/`);
    const synced = res?.data?.synced_count ?? 0;
    const failed = res?.data?.failed_count ?? 0;
    if (failed) {
      const names = (res?.data?.items || [])
        .filter((i: any) => i.status === "failed")
        .map((i: any) => i.product_name || `Variant ${i.variant_id}`)
        .join(", ");
      message.warning(`Synced ${synced} item(s); ${failed} failed: ${names}`, 8);
      return undefined;
    }
    message.success(`Synced ${synced} item(s) to Zoho`);
    return true;
  } catch (error: any) {
    const data = error?.response?.data;
    const failedNames = (data?.items || [])
      .filter((i: any) => i.status === "failed")
      .map((i: any) => i.error)
      .join("; ");
    message.error(failedNames || data?.detail || "Item sync failed");
    return undefined;
  }
};

// Escalation-scoped retry: requeues the failed Zoho docs an escalation spawned AND the failed
// carrier ops on the legs it re-planned, in one action. Same response shape as the stage retry.
export const retryEscalation = async (
  escalationId: number
): Promise<SalesOrderDetail | undefined> => {
  try {
    const res = await axios.post(`/oms/escalations/${escalationId}/retry/`);
    reportRetry(res?.data?.retry, "this escalation");
    return res?.data as SalesOrderDetail;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Retry failed");
    return undefined;
  }
};

// Shared toast for a retry summary — "Requeued N documents and M bookings", or that there was
// nothing failed to retry.
const reportRetry = (retry: any, scope: string) => {
  const docs = retry?.documents_requeued?.length ?? 0;
  const bookings = retry?.bookings_requeued?.length ?? 0;
  if (!docs && !bookings) {
    message.info(`Nothing to retry — ${scope} has no failed documents or bookings.`);
    return;
  }
  const parts = [];
  if (docs) parts.push(`${docs} document${docs > 1 ? "s" : ""}`);
  if (bookings) parts.push(`${bookings} booking${bookings > 1 ? "s" : ""}`);
  // The endpoint requeues and returns — the backend's background pass is what actually re-sends
  // them, so the order in this response still shows the failure. Say so, or the operator reads the
  // unchanged screen as "the retry did nothing" and clicks again.
  message.success(`Requeued ${parts.join(" and ")} — retrying in the background, refresh shortly`);
};

// Make sure ONE customer payment exists in Zoho, for when legacy's capture-time sync failed and the
// order's pre-ship settlement gate can never open. Idempotent — the backend looks Zoho up by the
// payment's reference first, so a repeat click reports `present` instead of creating a second payment.
//
// Deliberately does NOT requeue the outbox: this only makes the money exist in Zoho. Re-running the
// payment gate is a separate, explicit action.
export const syncPaymentToZoho = async (
  eptId: number
): Promise<{ so?: SalesOrderDetail; result?: PaymentSyncResult }> => {
  try {
    const res = await axios.post(`/oms/payments/${eptId}/sync/`);
    const result = res?.data?.payment_sync as PaymentSyncResult | undefined;
    reportPaymentSync(result);
    return { so: res?.data?.id ? (res.data as SalesOrderDetail) : undefined, result };
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Payment sync failed");
    return {};
  }
};

// Every verdict gets a distinct message: the point of this button is that "nothing happened" and
// "money was created" must never look the same to the operator.
const reportPaymentSync = (result?: PaymentSyncResult) => {
  if (!result) return;
  switch (result.status) {
    case "pushed":
      message.success(
        `Payment created in Zoho${
          result.zoho_payment?.payment_number ? ` (${result.zoho_payment.payment_number})` : ""
        }`
      );
      return;
    case "present":
      message.info("Already in Zoho — nothing pushed.");
      return;
    case "stuck":
    case "check_unavailable":
    case "error":
      message.error(result.detail);
      return;
    default:
      // The skipped_* / not_oms_managed refusals: deliberate no-ops, each with a reason worth reading.
      message.warning(result.detail);
  }
};

// ---------------------------------------------------------------------------
// Manual overrides (/oms/overrides) — settle an outbox row whose write can never
// succeed. Not reachable from the nav; see pages/oms/overrides.tsx.
// ---------------------------------------------------------------------------
export type OutboxRow = {
  kind: "document" | "logistics";
  id: number;
  state: string;
  attempt_count?: number;
  error?: unknown;             // provider-derived — always render via fmtText
  request?: unknown;           // the exact body we sent the provider
  response?: unknown;          // its full reply
  manually_resolved?: { reason?: string; actor?: string; from_state?: string; at?: string } | null;
  sales_order_id?: number | null;
  operation_stage_id?: number | null;
  // document only
  doc_type?: string;
  doc_type_display?: string;
  zoho_doc_id?: string | null;
  // logistics only
  op?: string;
  operation_type?: string | null;
  booking_id?: number | null;
  booking_lifecycle?: string | null;
  provider_ref?: string | null;
};

export const lookupOutboxRow = async (
  kind: "document" | "logistics",
  id: number
): Promise<OutboxRow | undefined> => {
  try {
    const res = await axios.get("/oms/overrides/lookup/", { params: { kind, id } });
    return res?.data as OutboxRow;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not find that row");
    return undefined;
  }
};

// Every unsettled document + carrier op on one order — the way in when you have an order
// number and no outbox id (which is the normal situation when an order goes red).
export const lookupOrderRows = async (salesOrderId: number): Promise<OutboxRow[]> => {
  try {
    const res = await axios.get("/oms/overrides/lookup/", {
      params: { sales_order_id: salesOrderId },
    });
    return (res?.data?.rows ?? []) as OutboxRow[];
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not load that order");
    return [];
  }
};

export const forceResolve = async (
  kind: "document" | "logistics",
  id: number,
  reason: string,
  zohoDocId?: string
): Promise<OutboxRow | undefined> => {
  const url =
    kind === "document"
      ? `/oms/overrides/documents/${id}/resolve/`
      : `/oms/overrides/logistics-ops/${id}/resolve/`;
  try {
    const res = await axios.post(url, { reason, zoho_doc_id: zohoDocId });
    message.success("Marked as resolved");
    return res?.data as OutboxRow;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not resolve that row");
    return undefined;
  }
};

// ── CRM issue thread ─────────────────────────────────────────────────────────
// Escalations raised on the order + the CRM team's follow-up notes. The notes are read live from
// Kylas on every call (it owns them, and they get edited there), so this is not cached client-side.

export const getCrmIssues = async (
  soId: number
): Promise<CrmIssueThread | undefined> => {
  try {
    const res = await axios.get(`/oms/sales-orders/${soId}/crm-issues/`);
    return res?.data as CrmIssueThread;
  } catch (error: any) {
    errorActions(error, "Could not load the CRM issue thread");
    return undefined;
  }
};

// Posts onto the Kylas escalation deal itself, so the CRM side reads the same thread.
export const addCrmNote = async (
  soId: number,
  dealId: string,
  text: string
): Promise<CrmIssueThread | undefined> => {
  try {
    const res = await axios.post(`/oms/sales-orders/${soId}/crm-issues/notes/`, {
      deal_id: dealId,
      text,
    });
    message.success("Note added to the CRM ticket");
    return res?.data as CrmIssueThread;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not post the note");
    return undefined;
  }
};
